import { useState } from 'react'
import { BookOpen, CheckCircle, Clock, ExternalLink, KeyRound } from 'lucide-react'
import { BUY_TEST_SERIES_URL } from '@/features/payments'
import { trackUpgradeCtaClicked } from '@/shared/utils/analytics'
import type { TestSeriesResponse } from '../types'
import type { SeriesAccess } from '../utils/seriesAccess'
import { getSeriesImage } from '../utils/seriesMetadata'

interface TestSeriesCardProps {
  series: TestSeriesResponse
  /** null while the user's activation keys are still unknown */
  access: SeriesAccess | null
  onBrowse: (series: TestSeriesResponse) => void
  onActivate: (series: TestSeriesResponse) => void
}

export default function TestSeriesCard({ series, access, onBrowse, onActivate }: TestSeriesCardProps) {
  const imageUrl = getSeriesImage(series)
  const [imageFailed, setImageFailed] = useState(false)

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      {/* Cover image is square (1024x1024) */}
      <div className="aspect-square w-full bg-gray-100">
        {imageUrl && !imageFailed ? (
          <img
            src={imageUrl}
            alt={series.seriesName}
            loading="lazy"
            onError={() => setImageFailed(true)}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <BookOpen className="h-12 w-12 text-gray-300" />
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-4 sm:p-6">
        <div className="mb-3 flex items-start justify-between gap-3">
          <h3 className="text-lg font-semibold text-gray-900">{series.seriesName}</h3>
          {access === 'ACTIVE' && (
            <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700">
              <CheckCircle className="h-3.5 w-3.5" />
              Activated
            </span>
          )}
          {access === 'EXPIRED' && (
            <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-orange-100 px-2.5 py-1 text-xs font-medium text-orange-700">
              <Clock className="h-3.5 w-3.5" />
              Expired
            </span>
          )}
        </div>

        {series.description && <p className="mb-4 text-sm text-gray-600">{series.description}</p>}

        <div className="mb-5 mt-auto flex flex-wrap gap-4 text-sm text-gray-500">
          <span className="inline-flex items-center gap-1.5">
            <BookOpen className="h-4 w-4" />
            {series.assessmentCount} {series.assessmentCount === 1 ? 'test' : 'tests'}
          </span>
          {!!series.seriesYear && (
            <span>
              Publication Year: <span className="font-medium text-gray-700">{series.seriesYear}</span>
            </span>
          )}
        </div>

        {access === 'ACTIVE' ? (
          <button
            onClick={() => onBrowse(series)}
            className="w-full rounded-lg bg-blue-600 py-2.5 font-medium text-white transition-colors hover:bg-blue-700"
          >
            Browse
          </button>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <a
              href={BUY_TEST_SERIES_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackUpgradeCtaClicked(`test_series_buy_${series.seriesCode}`)}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 py-2.5 font-medium text-white transition-colors hover:bg-blue-700"
            >
              Buy
              <ExternalLink className="h-4 w-4" />
            </a>
            <button
              onClick={() => onActivate(series)}
              disabled={access === null}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-600 py-2.5 font-medium text-blue-600 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <KeyRound className="h-4 w-4" />
              Activate
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
